import cn from "classnames";
import { Field } from "components/shared/form";
import UploadSigningFiles from "components/shared/uploadSigningFiles";
import { getInitialStateFromConfig } from "helpers/form";
import Tooltip from "rc-tooltip";
import React, { useEffect, useState } from "react";
import { uploadAccountantSigningFiles, uploadSigningFiles } from "sources/books/legalPersons";
import LegalPersonLogo from "./logo";

const config = {
    position: {
        // field
        label: "Должность",
        inputProps: { name: "position" },
    },
    lastName: {
        // field
        label: "Фамилия",
        inputProps: { name: "lastName" },
    },
    firstName: {
        // field
        label: "Имя",
        inputProps: { name: "firstName" },
    },
    patronymic: {
        // field
        label: "Отчество",
        inputProps: { name: "patronymic" },
    },
    signature: {},
};

const initialState = getInitialStateFromConfig(config);

const renderValue = (label, value) => (
    <div className="col-sm-6 mb-2">
        <div className="text-muted small">{label}</div>
        <div>{value || "-"}</div>
    </div>
);

export default function Management(props) {
    const [editing, setEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [logoLoading, setLogoLoading] = useState(false);
    const [data, setData] = useState(props.data ?? initialState);

    useEffect(() => {
        setData(props.data ?? {});
    }, [props.data]);

    const updateSignFiles = (rest) => {
        props.modal({
            name: "default",
            title: "Файлы печати и подписи",
            CustomBody: UploadSigningFiles,
            bodyData: { refreshData: props.onUpdate, ...rest, id: props.id },
        });
    };
    const onFieldChange = ({ name, value }) => setData({ ...data, [name]: value });

    const renderField = (name) => <Field {...config[name]} value={data[name]} onChange={onFieldChange} />;

    const saveData = (e) => {
        e.preventDefault();
        setSaving(true);
        props
            .action(data)
            .then(() => props.onUpdate())
            .then(() => setEditing(false))
            .catch(() => undefined)
            .finally(() => setSaving(false));
    };

    const renderManagement = () => {
        if (!props.showManagement) {
            return null;
        }

        const { management } = props;
        const { signature } = props.data ?? {};
        return (
            <>
                <div className="mb-3">
                    <div className="font-weight-bold mb-2">
                        Руководство
                        <span className="col-1 text-center pl-1 col-form-label col-form-label-sm">
                            <Tooltip
                                overlay="Информация по руководителю организации заполняется автоматически при формировании первого документа в системе"
                                overlayClassName="book-form__tooltip"
                            >
                                <i className="icon-uEA59-question book-form__tooltip-icon" />
                            </Tooltip>
                        </span>
                    </div>
                    <div className="row">
                        {renderValue("Должность", management.position)}
                        {renderValue("Фамилия", management.lastName)}
                        {renderValue("Имя", management.firstName)}
                        {renderValue("Отчество", management.patronymic)}
                    </div>
                    <div className="row mb-2">
                        <label className="col-form-label col-form-label-sm col-12 text-muted">Подпись и печать</label>
                        <div className="col-12">
                            <div className="d-flex gap-2 mb-2 align-items-center">
                                {management.stamp && (
                                    <img
                                        className="mw-150"
                                        src={`data:image/png;base64,${management.stamp}`}
                                        alt="Печать"
                                    />
                                )}
                                {management.signature && (
                                    <img
                                        className="mw-150"
                                        src={`data:image/png;base64,${management.signature}`}
                                        alt="Подпись"
                                    />
                                )}
                            </div>
                            <div
                                className="btn btn-xs btn-secondary"
                                onClick={() => updateSignFiles({ action: uploadSigningFiles })}
                            >
                                Обновить печать и подпись
                            </div>
                        </div>
                    </div>
                </div>
                <div>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                        <div className="font-weight-bold">Бухгалтерия</div>
                        {!editing && (
                            <button className="btn btn-xs btn-secondary" type="button" onClick={() => setEditing(true)}>
                                Редактировать
                            </button>
                        )}
                    </div>
                    {editing ? (
                        <form onSubmit={saveData}>
                            {renderField("position")}
                            {renderField("lastName")}
                            {renderField("firstName")}
                            {renderField("patronymic")}
                            <div className="d-flex justify-content-end mb-3">
                                <button
                                    className="btn btn-sm btn-secondary mr-2"
                                    type="button"
                                    onClick={() => {
                                        setData(props.data ?? {});
                                        setEditing(false);
                                    }}
                                >
                                    Отмена
                                </button>
                                <button className="btn btn-sm btn-brand" type="submit" disabled={saving}>
                                    Сохранить
                                </button>
                            </div>
                        </form>
                    ) : (
                        <div className="row">
                            {renderValue("Должность", data.position)}
                            {renderValue("Фамилия", data.lastName)}
                            {renderValue("Имя", data.firstName)}
                            {renderValue("Отчество", data.patronymic)}
                        </div>
                    )}
                    <div className="row mb-2">
                        <label className="col-form-label col-form-label-sm col-12 text-muted">Подпись</label>
                        <div className="col-12">
                            <div className="d-flex gap-2 mb-2">
                                {signature && (
                                    <img className="mw-150" src={`data:image/png;base64,${signature}`} alt="Подпись" />
                                )}
                            </div>
                            <div
                                className="btn btn-xs btn-secondary"
                                onClick={() =>
                                    updateSignFiles({ action: uploadAccountantSigningFiles, onlySignature: true })
                                }
                            >
                                Обновить подпись
                            </div>
                        </div>
                    </div>
                </div>
            </>
        );
    };

    return (
        <div className={cn("card card-body mb-3", { loading: saving })}>
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="mb-0">Документы и подписи</h5>
            </div>
            <LegalPersonLogo
                data={props.legalPerson}
                id={props.id}
                loading={logoLoading}
                setLoading={setLogoLoading}
                modal={props.modal}
                onUpdate={props.onUpdate}
            />
            {renderManagement()}
        </div>
    );
}
